import { useEffect } from 'react';
import { ArrowRight, X } from 'lucide-react';
import type { Activity, ScheduleModel } from '../../types/schedule';
import { formatCurrency, formatDate } from './chartPrimitives';

interface ActivityInspectorProps {
  activity: Activity;
  schedule: ScheduleModel;
  baseline?: Activity;
  onClose: () => void;
  onNavigate: (activityId: string) => void;
}

function Row({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="inspector-row">
      <dt>{label}</dt>
      <dd className="tnum" style={tone ? { color: tone } : undefined}>
        {value}
      </dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <h4 className="eyebrow mb-2" style={{ color: 'var(--text-muted)' }}>
        {title}
      </h4>
      <dl>{children}</dl>
    </div>
  );
}

/**
 * Right-hand detail panel. Opens from any chart, table row or network node,
 * and links onward through the activity's own logic so the network can be
 * walked without going back to a list.
 */
export function ActivityInspector({
  activity,
  schedule,
  baseline,
  onClose,
  onNavigate,
}: ActivityInspectorProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const wbs = activity.wbsId ? schedule.wbsById.get(activity.wbsId) : null;
  const floatTone =
    activity.totalFloatDays < 0
      ? 'var(--status-fail)'
      : activity.totalFloatDays === 0
        ? 'var(--mr-vivid-blue)'
        : activity.totalFloatDays > 44
          ? 'var(--status-warn)'
          : undefined;

  const slipDays =
    baseline?.finish && activity.finish
      ? Math.round(
          (activity.finish.getTime() - baseline.finish.getTime()) / 86_400_000
        )
      : null;

  const linked = (relationshipList: typeof activity.predecessors, direction: 'pred' | 'succ') =>
    relationshipList.slice(0, 12).map((rel) => {
      const otherId = direction === 'pred' ? rel.predecessorId : rel.successorId;
      const other = schedule.activityById.get(otherId);
      return (
        <button
          key={`${otherId}-${rel.type}`}
          className="flex w-full items-center gap-2 py-1.5 text-left text-xs transition-colors hover:opacity-70"
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
          onClick={() => other && onNavigate(other.id)}
          disabled={!other}
        >
          <span
            className="tnum shrink-0 rounded-sm px-1 py-0.5 text-[0.6rem] font-semibold"
            style={{
              background: 'var(--surface-inset)',
              color: rel.type === 'FS' ? 'var(--text-muted)' : 'var(--status-warn)',
            }}
          >
            {rel.type}
            {Math.abs(rel.lagDays) >= 0.5 &&
              ` ${rel.lagDays > 0 ? '+' : ''}${rel.lagDays.toFixed(0)}d`}
          </span>
          <span className="truncate" style={{ color: 'var(--text-secondary)' }}>
            {other ? `${other.code} ${other.name}` : 'Not in this file'}
          </span>
          {other && <ArrowRight className="ml-auto h-3 w-3 shrink-0" style={{ color: 'var(--text-muted)' }} />}
        </button>
      );
    });

  return (
    <aside className="inspector" role="complementary" aria-label="Activity detail">
      <div
        className="flex items-start justify-between gap-3 border-b p-4"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <div className="min-w-0">
          <p className="tnum text-xs font-semibold" style={{ color: 'var(--mr-vivid-blue)' }}>
            {activity.code}
          </p>
          <h3 className="mt-0.5 text-sm font-semibold leading-snug" style={{ color: 'var(--text-primary)' }}>
            {activity.name}
          </h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span
              className={`chip ${
                activity.isComplete ? 'chip-pass' : activity.isInProgress ? 'chip-info' : 'chip-na'
              }`}
            >
              {activity.status}
            </span>
            {activity.isCritical && !activity.isComplete && (
              <span className="chip chip-fail">Critical</span>
            )}
            {activity.hasHardConstraint && (
              <span className="chip chip-warn">Hard constraint</span>
            )}
            {activity.isMilestone && <span className="chip chip-na">Milestone</span>}
          </div>
        </div>
        <button
          className="icon-button shrink-0"
          onClick={onClose}
          aria-label="Close activity detail"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <Section title="Dates">
          <Row label="Start" value={formatDate(activity.start)} />
          <Row label="Finish" value={formatDate(activity.finish)} />
          {activity.actualStart && (
            <Row label="Actual start" value={formatDate(activity.actualStart)} />
          )}
          {activity.actualFinish && (
            <Row label="Actual finish" value={formatDate(activity.actualFinish)} />
          )}
          {baseline && (
            <>
              <Row label="Baseline finish" value={formatDate(baseline.finish)} />
              {slipDays !== null && (
                <Row
                  label="Variance"
                  value={`${slipDays > 0 ? '+' : ''}${slipDays} days`}
                  tone={
                    slipDays > 0
                      ? 'var(--status-fail)'
                      : slipDays < 0
                        ? 'var(--status-pass)'
                        : undefined
                  }
                />
              )}
            </>
          )}
          {activity.constraintType && (
            <Row
              label="Constraint"
              value={`${activity.constraintType}${
                activity.constraintDate ? ` · ${formatDate(activity.constraintDate)}` : ''
              }`}
              tone={activity.hasHardConstraint ? 'var(--status-warn)' : undefined}
            />
          )}
        </Section>

        <Section title="Duration and float">
          <Row
            label="Original duration"
            value={`${activity.originalDurationDays.toFixed(1)} days`}
          />
          <Row
            label="Remaining"
            value={`${activity.remainingDurationDays.toFixed(1)} days`}
          />
          <Row
            label="Total float"
            value={`${activity.totalFloatDays.toFixed(1)} days`}
            tone={floatTone}
          />
          <Row label="Free float" value={`${activity.freeFloatDays.toFixed(1)} days`} />
          <Row label="Complete" value={`${activity.percentComplete.toFixed(0)}%`} />
        </Section>

        {(activity.budgetCost > 0 || activity.resources.length > 0) && (
          <Section title="Cost and resource">
            <Row label="Budget" value={formatCurrency(activity.budgetCost)} />
            <Row label="Actual" value={formatCurrency(activity.actualCost)} />
            <Row label="Remaining" value={formatCurrency(activity.remainingCost)} />
            <Row label="Assignments" value={String(activity.resources.length)} />
          </Section>
        )}

        <Section title="Classification">
          <Row label="Type" value={activity.type} />
          {wbs && <Row label="WBS" value={wbs.name} />}
          <Row
            label="Calendar"
            value={
              activity.calendarId
                ? (schedule.calendars.get(activity.calendarId)?.name ?? '—')
                : '—'
            }
          />
          {Object.entries(activity.activityCodes).map(([type, value]) => (
            <Row key={type} label={type} value={value} />
          ))}
        </Section>

        <div className="mb-5">
          <h4 className="eyebrow mb-2" style={{ color: 'var(--text-muted)' }}>
            Predecessors ({activity.predecessors.length})
          </h4>
          {activity.predecessors.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--status-warn)' }}>
              No predecessor — this activity has an open start.
            </p>
          ) : (
            linked(activity.predecessors, 'pred')
          )}
        </div>

        <div>
          <h4 className="eyebrow mb-2" style={{ color: 'var(--text-muted)' }}>
            Successors ({activity.successors.length})
          </h4>
          {activity.successors.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--status-warn)' }}>
              No successor — delay here will not show in the finish date.
            </p>
          ) : (
            linked(activity.successors, 'succ')
          )}
        </div>
      </div>
    </aside>
  );
}
